import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RotateCw } from "lucide-react";
import { EmptyIllustration } from "./kit/EmptyIllustration";
import { TopBar, DestinationRow, NumberTile, SecondaryAction, MoreSection } from "./kit";
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";
import FilterBox from "./FilterBox";
import StatCharts from "./StatCharts";
import DeeperLook from "./DeeperLook";
import LogGroundMapBand, { BasemapCredit } from "./LogGroundMapBand";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { usePageWidth } from "../context/PageWidthContext";
import { useDashFilters, filterQuery } from "../hooks/useDashFilters";
import { useMatchFilterOptions } from "../hooks/useMatchFilterOptions";
import { careerSections } from "../lib/careerFigures";
import api from "../lib/api";

/**
 * Issue #1147 (D225.1 option B, D228.4) — the Dashboard as its own ribbon
 * destination at `/insights`: career figures, six compact charts, the Season
 * filter on the face (Competition and Team behind More), and the map of the
 * grounds the player has played.
 *
 * Record (`/dashboard`) keeps This season; this screen owns the career view
 * that My Stats used to give. It reads two filtered endpoints,
 * `GET /stats/summary` (the totals) and `GET /analytics/career` (the trend for
 * the charts, and the keeper's own match count), with the same
 * season/competition/team query Record sends, and persists the filters under
 * the same `nw_dash_*` keys, so Season chosen on Record is Season here.
 *
 * #1410 (D367.18): this is also the one place for the deeper stats and graphs that Record's
 * "Show more" used to hold (`DeeperLook.jsx`), so a figure is on one screen, once.
 *
 * `GET /venues/played` takes no filter, so the map shows every ground the
 * player has played whatever the filters say; the line under it says so.
 *
 * STATES: loading (shapes), failed (a card with Try again, kept on the page,
 * D57: no toast), no match yet, and one match (one point per chart, no
 * minimum-count words). This screen has no gold element (R-003).
 *
 * Player only: `ProtectedRoute roles={["player"]}` bounces a coach or admin.
 */
const cardStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  padding: 16,
  background: "var(--nw-surface)",
  border: "1px solid var(--nw-line)",
  borderRadius: "var(--nw-radius-card)",
};

/** "3 grounds in 2 countries", or just the count when no ground names a country. */
function groundsLine(played) {
  const n = played.length;
  const countries = new Set(played.map((v) => (v.country || "").trim()).filter(Boolean)).size;
  const grounds = `${n} ${n === 1 ? "ground" : "grounds"}`;
  return countries ? `${grounds} in ${countries} ${countries === 1 ? "country" : "countries"}` : grounds;
}

/** The board's filtered-empty line: "No matches for [COMPETITION] in [SEASON]." */
function noMatchesFor({ season, comp, team }) {
  const what = [comp, team].filter((v) => v !== "all").join(" · ");
  return `No matches${what ? ` for ${what}` : ""}${season !== "all" ? ` in ${season}` : ""}.`;
}

export default function RecordDashboardScreen() {
  // #1431 (archetype C, D386): the wide column, one column across the tablet tier, two-up from 1024.
  usePageWidth("wide");
  const { setActive: setChromeless } = useChromelessOverride();
  useEffect(() => {
    setChromeless(true);
    return () => setChromeless(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filters = useDashFilters();
  const { season, comp, team, setSeason, setComp, setTeam } = filters;
  const { seasonOptions, compOptions, teamOptions } = useMatchFilterOptions();
  const filterActive = season !== "all" || comp !== "all" || team !== "all";
  const qs = filterQuery(filters);

  const [data, setData] = useState(null); // { summary, career }
  const [failed, setFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setData(null);
    Promise.all([api.get(`/stats/summary${qs}`), api.get(`/analytics/career${qs}`)])
      .then(([s, c]) => {
        if (!cancelled) setData({ summary: s.data, career: c.data });
      })
      .catch((e) => {
        if (cancelled) return;
        setFailed(true);
        console.error("RecordDashboardScreen: failed to load the career figures", e);
      });
    return () => { cancelled = true; };
  }, [qs, reloadKey]);

  // The map is the player's whole played history; no filter reaches it.
  const [played, setPlayed] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api
      .get("/venues/played")
      .then((r) => { if (!cancelled) setPlayed(r.data || []); })
      .catch(() => { /* the band still draws, with no pins */ });
    return () => { cancelled = true; };
  }, []);
  const pins = played.map((v) => ({ ...v, name: v.ground }));
  // A pin has nothing to open here; the ground's own card lives on Grounds.
  const onPick = useCallback(() => {}, []);

  const loading = data === null && !failed;
  const summary = data?.summary;
  const noMatches = !!summary && summary.matches === 0;

  return (
    <div data-testid="insights-page" style={{ color: "var(--nw-ink)" }}>
      <span data-testid="career-new-screen" className="sr-only" aria-hidden="true" />
      <TopBar
        variant="white"
        homeHref="/dashboard"
        homeLabel="CricketCompass — go to your dashboard"
        endControls={
          <>
            <NotificationsBell compact surface="white" />
            <AccessibilityMenu compact surface="white" />
            <AccountMenu empty surface="white" hasRibbon />
          </>
        }
        data-testid="career-topbar"
      />
      <DestinationRow current="dashboard" />

      <div className="flex flex-col" style={{ gap: "var(--nw-space-stack)", padding: "22px 20px 20px" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div className="nw-eyebrow" data-testid="insights-eyebrow">Your record</div>
          <h1 className="nw-title-lg" style={{ margin: 0 }}>Dashboard</h1>
          <p className="nw-help" data-testid="insights-lede" style={{ margin: 0 }}>
            Every season, every ground: the figures, the totals and the charts.
          </p>
        </div>

        {/* #1431: from 1024 the Season box sits beside the More row (index.css, `.nw-wide-grid`). */}
        <div className="nw-wide-grid nw-wide-grid--top" data-testid="insights-filters">
          <div style={{ display: "flex", gap: 8 }}>
            <FilterBox testId="insights-filter-season" label="Season" value={season} onChange={setSeason} options={seasonOptions} allLabel="All seasons" />
          </div>
          <MoreSection screen="career" label="More filters · competition, team" lessLabel="Fewer filters" data-testid="career-more">
            <div style={{ display: "flex", gap: 8 }}>
              <FilterBox testId="insights-filter-comp" label="Competition" value={comp} onChange={setComp} options={compOptions} allLabel="All" />
              <FilterBox testId="insights-filter-team" label="Team" value={team} onChange={setTeam} options={teamOptions} allLabel="All" />
            </div>
          </MoreSection>
        </div>
        {filterActive && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: -6 }}>
            <div className="nw-help" data-testid="insights-filter-line">
              Showing {[season !== "all" ? season : null, comp !== "all" ? comp : null, team !== "all" ? team : null].filter(Boolean).join(" · ")}.
            </div>
            <SecondaryAction
              variant="link"
              data-testid="insights-clear"
              onClick={() => { setSeason("all"); setComp("all"); setTeam("all"); }}
              style={{ fontSize: 13, color: "var(--nw-navy)", flexShrink: 0 }}
            >
              Clear filters
            </SecondaryAction>
          </div>
        )}

        {failed && (
          <div data-testid="insights-error" style={{ ...cardStyle, gap: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <svg data-testid="insights-error-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#B42318" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
                <path d="M2 2l20 20" />
                <path d="M5.8 5.8A7 7 0 0 0 7 19h11a4 4 0 0 0 1.9-.5" />
                <path d="M22 15a4 4 0 0 0-5-3.9 7 7 0 0 0-8.7-4.9" />
              </svg>
              <div style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>Couldn&#x27;t load your figures</div>
            </div>
            <div className="nw-help">Nothing has been lost — we just couldn&#x27;t reach the server.</div>
            <SecondaryAction
              data-testid="insights-retry"
              onClick={() => setReloadKey((k) => k + 1)}
              style={{ alignSelf: "flex-start", minHeight: 44, padding: "0 14px", border: "1px solid var(--nw-line-strong)", borderRadius: "var(--nw-radius-control)", color: "var(--nw-navy)", fontSize: 14, fontWeight: 600, gap: 6 }}
            >
              <RotateCw size={14} aria-hidden="true" />
              Try again
            </SecondaryAction>
          </div>
        )}

        {loading && (
          <div data-testid="insights-loading" aria-busy="true" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[96, 96, 160].map((h, i) => (
              <div key={i} style={{ height: h, background: "var(--nw-line-faint)", borderRadius: "var(--nw-radius-card)" }} />
            ))}
          </div>
        )}

        {noMatches && (
          <div
            data-testid="insights-empty"
            style={{ ...cardStyle, alignItems: "center", gap: 10, padding: "28px 20px", textAlign: "center", fontSize: 14, lineHeight: 1.5, color: "#475569" }}
          >
            {/* #1618 (D442): no match yet is the scorebook drawing; a filter with nothing in it keeps the magnifier tile. */}
            {filterActive ? (
              <div
                data-testid="insights-state-icon"
                style={{ width: 52, height: 52, borderRadius: 16, background: "#E8F1FB", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#0B2E59" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M20 20l-3.5-3.5" />
                </svg>
              </div>
            ) : (
              <EmptyIllustration name="matches" />
            )}
            {filterActive ? (
              <>
                <div style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>{noMatchesFor({ season, comp, team })}</div>
                <div>Change a filter to see your figures.</div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 600, fontSize: 15, color: "var(--nw-ink)" }}>Nothing on it yet.</div>
                <div>Log a match and your first figures, charts and pin land here.</div>
              </>
            )}
          </div>
        )}

        {(() => {
          const sections = summary && !noMatches ? careerSections(summary, data.career.wicketkeeping, data.career) : null;
          const section = (sec) => (
            <div
              key={sec.key}
              data-testid={sec.key === "batting" ? "insights-highlights" : `insights-${sec.key}`}
              // #1431: from 1024 highlights and bowling take the left column (bowling two rows tall), fielding and keeping stack in the right.
              {...(sec.key === "fielding" || sec.key === "keeping"
                ? { "data-wide-col": "2", "data-wide-start": "true" }
                : sec.key === "bowling"
                  ? { "data-wide-col": "1", "data-wide-span": "2", "data-wide-start": "true" } // beside Fielding and Keeping, which stack in column 2
                  : { "data-wide-col": "1", "data-wide-start": "true" })}
              style={cardStyle}
            >
              <div className="nw-section">{sec.title}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
                {sec.tiles.map((t) => (
                  <NumberTile
                    key={t.key}
                    variant="tile"
                    label={t.label}
                    value={t.value}
                    restsOn={t.restsOn}
                    // Top-aligned, two label lines reserved: neither a "Rests on" line
                    // nor a label wrapping at 360 may push a figure out of line with
                    // its row (review F9).
                    labelLines={2}
                    style={{ justifyContent: "flex-start" }}
                    data-testid={t.testId}
                  />
                ))}
              </div>
              {sec.note && <div className="nw-help">{sec.note}</div>}
            </div>
          );
          return (
            <div className="nw-wide-grid" data-testid="insights-grid">
              {sections && section(sections[0])}
              {sections && <StatCharts trend={data.career.trend || []} />}
              {sections && (data.career.trend || []).length === 1 && (
                <div data-testid="insights-one-point" data-wide="full" style={{ fontSize: 11, lineHeight: 1.5, color: "var(--nw-ink-faint)" }}>
                  One point, no line yet.
                </div>
              )}
              <div data-testid="insights-map-band" {...(sections ? { "data-wide-col": "2" } : { "data-wide": "full" })} style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)", rowGap: 8 }}>
                <div className="nw-section" style={{ gridRow: 1 }}>Where I&#x27;ve played</div>
                {/* #1622 (D439): a preview of the most-played grounds; a tap opens Grounds on Played. */}
                <Link
                  to="/grounds-map?view=played"
                  data-testid="insights-map-explore"
                  aria-label="Tap to explore where you've played"
                  style={{ gridRow: 2, gridColumn: 1, display: "block", position: "relative", borderRadius: "var(--nw-radius-card)", overflow: "hidden" }}
                >
                  <LogGroundMapBand rows={pins} onPick={onPick} testId="insights-map" fit preview />
                  <span
                    data-testid="insights-map-explore-chip"
                    style={{
                      position: "absolute", right: 8, bottom: 8, zIndex: 1000, display: "inline-flex", alignItems: "center", minHeight: 44, padding: "0 14px",
                      borderRadius: 999, background: "var(--nw-navy)", color: "var(--nw-surface)", fontSize: 14, fontWeight: 600, pointerEvents: "none",
                    }}
                  >
                    Tap to explore
                  </span>
                </Link>
                <div style={{ gridRow: 3, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                  <span data-testid="insights-map-count" style={{ fontSize: 13, color: "var(--nw-ink)" }}>{groundsLine(played)}</span>
                  <Link
                    to="/grounds-map?view=played"
                    data-testid="insights-map-open"
                    style={{ display: "inline-flex", alignItems: "center", minHeight: 44, fontSize: 14, fontWeight: 600, color: "var(--nw-navy)", textDecoration: "underline" }}
                  >
                    Open in Grounds
                  </Link>
                </div>
                {played.length > 0 && (
                  <ol data-testid="insights-top-grounds" style={{ gridRow: 4, listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: 6 }}>
                    {played.slice(0, 3).map((v) => {
                      const place = [v.city, v.country].map((x) => (x || "").trim()).filter(Boolean).join(", ");
                      return (
                        <li key={v.ground_id || v.ground} style={{ display: "flex", flexDirection: "column", fontSize: 13, color: "var(--nw-ink)" }}>
                          <span style={{ fontWeight: 600 }}>{v.ground}</span>
                          <span>{v.matches} {v.matches === 1 ? "match" : "matches"}</span>
                          {place && <span data-testid="insights-top-place" style={{ color: "var(--nw-ink-muted)" }}>{place}</span>}
                        </li>
                      );
                    })}
                  </ol>
                )}
                {/* #1622 round 2: the tiles' credit shares the map's grid cell (bottom left, clear of the chip) but comes last in
                    the DOM, so Tab goes explore link, Open in Grounds, then the credit links. Outside the link and the inert box. */}
                <BasemapCredit style={{ gridRow: 2, gridColumn: 1, alignSelf: "end", justifySelf: "start", zIndex: 1001, margin: 8, padding: "2px 6px", fontSize: 12, lineHeight: 1.4, background: "rgba(255,255,255,0.85)", color: "var(--nw-ink)", borderRadius: 4 }} />
                <div className="nw-help" style={{ gridRow: 5 }}>Every ground you have played, whatever the filters say.</div>
              </div>
              {sections && sections.slice(1).map(section)}
              {/* #1410 (D367.18): what Record's "Show more" drew, once, after the board's own sections. */}
              {sections && <DeeperLook career={data.career} />}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
